import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { formatDate, Page, State } from '../components';
interface Handoff {
  id: string;
  reason: string;
  status: string;
  createdAt: string;
}
export function HandoffsPage() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['handoffs'],
    queryFn: () => api<{ data: Handoff[] }>('/api/v1/handoffs'),
  });
  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/api/v1/handoffs/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['handoffs'] }),
  });
  return (
    <Page
      title="Human handoffs"
      description="Accept and resolve requests without triggering external side effects."
    >
      <State loading={query.isLoading} error={query.error} empty={query.data?.data.length === 0}>
        {update.isError && (
          <p className="mutation-error" role="alert">
            Could not update this handoff:{' '}
            {query.data?.data.find((row) => row.id === update.variables?.id)?.reason}. Choose the
            status again to retry.
          </p>
        )}
        <div className="card table-scroll">
          <table>
            <thead>
              <tr>
                <th>Reason</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Update</th>
              </tr>
            </thead>
            <tbody>
              {query.data?.data.map((row) => (
                <tr key={row.id}>
                  <td>{row.reason}</td>
                  <td>
                    <span className="badge">{row.status}</span>
                  </td>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>
                    <select
                      aria-label={`Status for handoff ${row.id}`}
                      value={row.status}
                      disabled={update.isPending}
                      onChange={(e) => update.mutate({ id: row.id, status: e.target.value })}
                    >
                      <option>REQUESTED</option>
                      <option>ACCEPTED</option>
                      <option>RESOLVED</option>
                      <option>CANCELED</option>
                    </select>
                    {update.isPending && update.variables?.id === row.id && (
                      <p className="mutation-status" role="status">
                        Saving status...
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </State>
    </Page>
  );
}
